// Admin Portal
// src/pages/LMS/LMSDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent, CardActions,
  Chip, IconButton, Menu, MenuItem, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, Alert, Pagination, CircularProgress, Stack,
  Tooltip, Divider, LinearProgress, ListItemIcon, ListItemText,
  Avatar, Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Assessment as AnalyticsIcon,
  Visibility as ViewIcon,
  Publish as PublishIcon,
  Pause as PauseIcon,
  School as SchoolIcon,
  Book as BookIcon,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  WorkspacePremium as CertificateIcon,
  Fingerprint as IdIcon,
  Code as CodeIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { hasPermission } from '../../utils/permissionUtils';

const LMSDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  // State
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, course: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Create form
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    short_description: '',
    category: '',
    difficulty_level: 'beginner',
    estimated_duration: 0,
    passing_score: 70,
    is_certificate_enabled: true
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Check permissions
  useEffect(() => {
    const checkPerms = async () => {
      try {
        const viewPerm = await hasPermission('lms', 'view');
        const modifyPerm = await hasPermission('lms', 'add|edit');
        const deletePerm = await hasPermission('lms', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPerms();
  }, []);

  // Load courses and stats
  useEffect(() => {
    if (canView && chatbotId) {
      loadCourses();
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, chatbotId, page, statusFilter, categoryFilter, searchTerm]);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 12,
        chatbot_id: chatbotId
      };

      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/api/lms/courses', { params });
      setCourses(response.data.data);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Error loading courses', error);
      showSnackbar('Error loading courses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = { chatbot_id: chatbotId, period: '30_days' };
      const response = await axiosInstance.get('/api/lms/analytics', { params });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Thumbnail handlers
  const handleThumbnailSelect = (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar('Please upload a valid image file (JPG, PNG, GIF, WEBP)', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('Image size must be less than 5MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setThumbnailPreview(e.target.result);
    reader.readAsDataURL(file);
    setThumbnailFile(file);
  };

  const handleRemoveThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const handleCreateCourse = async () => {
    if (!newCourse.title.trim()) {
      showSnackbar('Course title is required', 'error');
      return;
    }

    try {
      const params = { chatbot_id: chatbotId };
      const response = await axiosInstance.post('/api/lms/courses', newCourse, { params });
      const courseIdentity = response.data.data.course_identity;

      // Upload thumbnail if selected
      if (thumbnailFile) {
        setUploadingThumbnail(true);
        try {
          const formData = new FormData();
          formData.append('file', thumbnailFile);
          await axiosInstance.post(
            `/api/lms/courses/${courseIdentity}/thumbnail`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } catch (thumbError) {
          console.error('Error uploading thumbnail:', thumbError);
        } finally {
          setUploadingThumbnail(false);
        }
      }

      showSnackbar('Course created successfully', 'success');
      setCreateDialog(false);
      resetForm();

      navigate(`/chatbot/${chatbotId}/lms/courses/${courseIdentity}/builder`);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error creating course', 'error');
    }
  };

  const resetForm = () => {
    setNewCourse({
      title: '',
      description: '',
      short_description: '',
      category: '',
      difficulty_level: 'beginner',
      estimated_duration: 0,
      passing_score: 70,
      is_certificate_enabled: true
    });
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const handleUpdateStatus = async (courseId, newStatus) => {
    try {
      const isPublishing = newStatus === 'published';
      await axiosInstance.post(`/api/lms/courses/${courseId}/publish`, {
        publish: isPublishing
      });
      showSnackbar(`Course ${isPublishing ? 'published' : 'unpublished'} successfully`, 'success');
      loadCourses();
      loadStats();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error updating status', 'error');
    }
    handleMenuClose();
  };

  const handleDuplicateCourse = async (courseId) => {
    try {
      await axiosInstance.post(`/api/lms/courses/${courseId}/duplicate`, {
        new_title: `${selectedCourse.title} (Copy)`
      });
      showSnackbar('Course duplicated successfully', 'success');
      loadCourses();
    } catch (error) {
      showSnackbar('Error duplicating course', 'error');
    }
    handleMenuClose();
  };

  const handleDeleteCourse = async () => {
    if (!deleteDialog.course) return;

    try {
      await axiosInstance.delete(`/api/lms/courses/${deleteDialog.course.id}`);
      showSnackbar('Course archived successfully', 'success');
      setDeleteDialog({ open: false, course: null });
      loadCourses();
      loadStats();
    } catch (error) {
      showSnackbar('Error deleting course', 'error');
    }
  };

  const handleMenuOpen = (event, course) => {
    setMenuAnchor(event.currentTarget);
    setSelectedCourse(course);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCourse(null);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusChip = (status) => {
    const colors = {
      draft: 'default',
      published: 'success',
      archived: 'error'
    };
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={colors[status] || 'default'}
        size="small"
      />
    );
  };

  const getDifficultyColor = (level) => {
    const colors = {
      beginner: 'success',
      intermediate: 'warning',
      advanced: 'error'
    };
    return colors[level] || 'default';
  };

  if (checkingPermissions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 6 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view Learning Management System.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
            LMS Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage courses, content, and student progress for your AI Chatbot
          </Typography>
        </Box>
        {canModify && (
          <Stack direction="row" spacing={2}>
            {/* NEW: Embed Pages Button */}
            <Button
              variant="outlined"
              startIcon={<CodeIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/lms/embed-pages`)}
              color="primary"
            >
              Create Embed Pages
            </Button>
            <Button
              variant="outlined"
              startIcon={<PeopleIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/lms/students`)}
            >
              Students
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/lms/enrollments`)}
            >
              Enrollments
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialog(true)}
            >
              Create Course
            </Button>
          </Stack>
        )}
      </Box>


      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <BookIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.course_stats?.total_courses || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Courses
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="success.main">
                  {stats.course_stats?.published_courses || 0} Published
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.student_stats?.total_students || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Students
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="success.main">
                  {stats.student_stats?.active_students || 0} Active Recently
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                    <TrophyIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.enrollment_stats?.completed_enrollments || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completions
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="success.main">
                  {stats.enrollment_stats?.completion_rate || 0}% Completion Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                    <CertificateIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.engagement_stats?.certificates_issued || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Certificates
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Issued All Time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Safety">Safety</MenuItem>
                <MenuItem value="Compliance">Compliance</MenuItem>
                <MenuItem value="Technical">Technical</MenuItem>
                <MenuItem value="Soft Skills">Soft Skills</MenuItem>
                <MenuItem value="Leadership">Leadership</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<TimelineIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/lms/analytics`)}
            >
              Analytics
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Course Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : courses.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No courses found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || statusFilter || categoryFilter
              ? 'Try adjusting your filters'
              : 'Get started by creating your first course'}
          </Typography>
          {canModify && !searchTerm && !statusFilter && !categoryFilter && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialog(true)}
            >
              Create Course
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {courses.map((course) => (
              <Grid item xs={12} sm={6} md={4} key={course.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: '0.3s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  {/* Course Thumbnail */}
                  {course.thumbnail_url && (
                    <Box
                      sx={{
                        height: 160,
                        backgroundImage: `url(${course.thumbnail_url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative'
                      }}
                    >
                      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        {getStatusChip(course.status)}
                      </Box>
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1 }}>
                    {!course.thumbnail_url && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        {getStatusChip(course.status)}
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, course)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    )}

                    <Tooltip title="Course Identity" arrow>
                        <Chip 
                            icon={<IdIcon sx={{ fontSize: '14px !important' }} />}
                            label={course.course_identity || `ID: ${course.id}`}
                            size="small" 
                            variant="outlined"
                            sx={{ mb: 1, fontSize: '0.7rem', height: 20 }}
                        />
                    </Tooltip>

                    <Typography variant="h6" gutterBottom noWrap title={course.title}>
                      {course.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: 40, overflow: 'hidden' }}>
                      {course.short_description || course.description || 'No description'}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      <Chip
                        label={course.difficulty_level}
                        size="small"
                        color={getDifficultyColor(course.difficulty_level)}
                      />
                      {course.category && (
                        <Chip label={course.category} size="small" variant="outlined" />
                      )}
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Modules
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {course.stats?.module_count || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Enrolled
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {course.stats?.total_enrolled || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Completed
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {course.stats?.completed || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Duration
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {course.estimated_duration || 0}h
                        </Typography>
                      </Grid>
                    </Grid>

                    {course.stats && (
                       <Box sx={{ mt: 2 }}>
                         <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                           <Typography variant="caption">Completion Rate</Typography>
                           <Typography variant="caption">
                             {course.stats.total_enrolled > 0 
                                ? Math.round((course.stats.completed / course.stats.total_enrolled) * 100) 
                                : 0}%
                           </Typography>
                         </Box>
                         <LinearProgress
                           variant="determinate"
                           value={course.stats.total_enrolled > 0 
                            ? (course.stats.completed / course.stats.total_enrolled) * 100 
                            : 0}
                           sx={{ height: 6, borderRadius: 3 }}
                         />
                       </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/chatbot/${chatbotId}/lms/courses/${course.course_identity}/builder`)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<PeopleIcon />}
                      onClick={() => navigate(`/chatbot/${chatbotId}/lms/courses/${course.course_identity}/enrollments`)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Students
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AnalyticsIcon />}
                      onClick={() => navigate(`/chatbot/${chatbotId}/lms/analytics/courses/${course.course_identity}`)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Analytics
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate(`/chatbot/${chatbotId}/lms/courses/${selectedCourse?.course_identity}/builder`);
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit Course</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleMenuClose();
          navigate(`/chatbot/${chatbotId}/lms/courses/${selectedCourse?.course_identity}/preview`);
        }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <Divider />
        {selectedCourse?.status === 'draft' && (
          <MenuItem onClick={() => handleUpdateStatus(selectedCourse.id, 'published')}>
            <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Publish</ListItemText>
          </MenuItem>
        )}
        {selectedCourse?.status === 'published' && (
          <MenuItem onClick={() => handleUpdateStatus(selectedCourse.id, 'draft')}>
            <ListItemIcon><PauseIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Unpublish</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDuplicateCourse(selectedCourse?.id)}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        {canDelete && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setDeleteDialog({ open: true, course: selectedCourse });
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Archive</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create Course Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Course</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Course Title"
                value={newCourse.title}
                onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Short Description"
                value={newCourse.short_description}
                onChange={(e) => setNewCourse({ ...newCourse, short_description: e.target.value })}
                placeholder="Brief overview (appears in course cards)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Description"
                value={newCourse.description}
                onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Course Thumbnail
              </Typography>
              <Box sx={{
                border: '2px dashed',
                borderColor: thumbnailPreview ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: 'grey.50',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' }
              }}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => handleThumbnailSelect(e.target.files[0])}
                  style={{ display: 'none' }}
                  id="create-thumbnail-upload"
                />
                <label htmlFor="create-thumbnail-upload" style={{ cursor: 'pointer', display: 'block' }}>
                  {thumbnailPreview ? (
                    <Box>
                      <img
                        src={thumbnailPreview}
                        alt="Course thumbnail preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 180,
                          borderRadius: 8,
                          objectFit: 'cover'
                        }}
                      />
                      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                        Click to change thumbnail
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Click to upload course thumbnail
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Recommended: 1280x720px (16:9) - Max 5MB
                      </Typography>
                    </Box>
                  )}
                </label>
                {thumbnailPreview && (
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => { e.preventDefault(); handleRemoveThumbnail(); }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newCourse.category}
                  onChange={(e) => setNewCourse({ ...newCourse, category: e.target.value })}
                  label="Category"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Safety">Safety</MenuItem>
                  <MenuItem value="Compliance">Compliance</MenuItem>
                  <MenuItem value="Technical">Technical</MenuItem>
                  <MenuItem value="Soft Skills">Soft Skills</MenuItem>
                  <MenuItem value="Leadership">Leadership</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={newCourse.difficulty_level}
                  onChange={(e) => setNewCourse({ ...newCourse, difficulty_level: e.target.value })}
                  label="Difficulty Level"
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Duration (hours)"
                value={newCourse.estimated_duration}
                onChange={(e) => setNewCourse({ ...newCourse, estimated_duration: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Passing Score (%)"
                value={newCourse.passing_score}
                onChange={(e) => setNewCourse({ ...newCourse, passing_score: parseFloat(e.target.value) || 70 })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCourse}
            disabled={uploadingThumbnail}
            startIcon={uploadingThumbnail ? <CircularProgress size={16} /> : null}
          >
            {uploadingThumbnail ? 'Creating...' : 'Create & Build'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, course: null })}>
        <DialogTitle>Archive Course</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to archive "<strong>{deleteDialog.course?.title}</strong>"?<br/>
            <Typography variant="caption" color="text.secondary">ID: {deleteDialog.course?.course_identity}</Typography>
            <br/><br/>
            This action will make the course unavailable to students but preserve all data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, course: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteCourse}>
            Archive
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LMSDashboard;